import { useCallback, useEffect, useRef } from 'react';

import { useSnackbar } from '../context/SnackbarContext.jsx';
import { useTranslation } from '../context/TranslationContext.jsx';
import logger from '../utils/logger';
import logError from '../utils/logError';

import { recoverInterruptedTest } from './useCheckRunningTests';

// Wait longer than the polling interval before retrying recovery.
const RECOVERY_RETRY_DELAY_MS = 10000;

/**
 * Requests recovery only for runs marked as recoverable by the backend.
 * Prevents duplicate requests from repeated polling.
 */
const useInterruptedTestRecovery = (testBaseUrl, isAuthenticated) => {
  const { openSnackbar } = useSnackbar();
  const { translate } = useTranslation();
  // /tests/running is polled often, so refs remember handled runs without causing rerenders.
  const requestedRunsRef = useRef(new Set());
  const notifiedRunsRef = useRef(new Set());
  const errorNotifiedRunsRef = useRef(new Set());
  const retryAfterRef = useRef(new Map());
  const translateRef = useRef(translate);
  const isAuthenticatedRef = useRef(isAuthenticated);
  const sessionRef = useRef(0);

  useEffect(() => {
    // Keep the recovery callback stable while using the latest committed translation.
    translateRef.current = translate;
  }, [translate]);

  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
    sessionRef.current += 1;

    if (!isAuthenticated) {
      // Clear recovery history on logout.
      requestedRunsRef.current.clear();
      notifiedRunsRef.current.clear();
      errorNotifiedRunsRef.current.clear();
      retryAfterRef.current.clear();
    }

    return () => {
      // Ignore pending requests after unmount.
      isAuthenticatedRef.current = false;
      sessionRef.current += 1;
    };
  }, [isAuthenticated]);

  return useCallback(
    (processing = []) => {
      if (!isAuthenticatedRef.current || !Array.isArray(processing)) return Promise.resolve();

      const session = sessionRef.current;

      return Promise.all(
        processing.map((run) => {
          if (!run || typeof run !== 'object') return Promise.resolve();
          // The backend decides whether a run was interrupted; the frontend never guesses.
          if (!run.recoveryAvailable || !run.id || !run.runId) return Promise.resolve();
          if (requestedRunsRef.current.has(run.runId)) return Promise.resolve();
          // Failed requests wait here instead of retrying on every two-second poll.
          if ((retryAfterRef.current.get(run.runId) ?? 0) > Date.now()) {
            return Promise.resolve();
          }

          // Remember submitted runs so later polls cannot send duplicates.
          requestedRunsRef.current.add(run.runId);

          return recoverInterruptedTest(testBaseUrl, run.id)
            .then((response) => {
              if (!isAuthenticatedRef.current || sessionRef.current !== session) return;
              retryAfterRef.current.delete(run.runId);

              // Notify only when the backend actually starts recovery.
              if (
                response?.data?.recoveryStarted === true &&
                !notifiedRunsRef.current.has(run.runId)
              ) {
                notifiedRunsRef.current.add(run.runId);
                openSnackbar(
                  'warning',
                  translateRef.current('resultsTable.messages.recoveringAfterRestart'),
                );
              }
            })
            .catch((error) => {
              // Ignore requests from an old login.
              if (!isAuthenticatedRef.current || sessionRef.current !== session) return;

              requestedRunsRef.current.delete(run.runId);
              retryAfterRef.current.set(run.runId, Date.now() + RECOVERY_RETRY_DELAY_MS);
              if (!errorNotifiedRunsRef.current.has(run.runId)) {
                errorNotifiedRunsRef.current.add(run.runId);
                openSnackbar(
                  'error',
                  translateRef.current('resultsTable.messages.recoveryTemporarilyUnavailable'),
                );
              }
              logger.error(
                logError(error, {
                  action: 'recoverInterruptedTest',
                  testId: run.id,
                  processingRunId: run.runId,
                }),
              );
            });
        }),
      );
    },
    [openSnackbar, testBaseUrl],
  );
};

export default useInterruptedTestRecovery;
